import {
  ExpressionValue,
  IElement,
  OneOfElements,
  OneOfPropertyElements,
} from '@shared/models/project.model';
import { IElementExpressionProperty } from '@shared/models/element-property.model';
import { getDisplayNumber } from '../../display/get-display-number.helper';
import {
  getElementProperties,
  isElementExpressionProperty,
} from '../../element-property.helpers';
import { projectGuards } from '../../guards/project-guards.helpers';
import { isOneOfPropertyElements } from '../../recursive-element.helpers';
import { guards } from '../../typeguards.helpers';
import { ProjectChangeType } from '../update-engine.models';
import type { ProjectChangeMap } from '../update-engine.models';
import { ElementQuantityName } from '@shared/models/element-quantities.model';

type PropertyResolvedChange =
  ProjectChangeMap[ProjectChangeType.ElementPropertyResolved];

/**
 * Build a partial element change for ElementPropertyResolved.
 * Handles both regular properties (element.properties) and quantity
 * properties (element.quantity) — writes resolved value to count or fallbackCount
 * and sets `formatted` from {@link getDisplayNumber} for the same resolved value.
 *
 * @param element - Current element state
 * @param change - Resolved value change payload
 * @returns Partial<OneOfElements> or undefined if not applicable
 */
export const elementPropertyResolvedToChange = (
  element: OneOfElements,
  change: PropertyResolvedChange,
): Partial<OneOfElements> | undefined => {
  if (!isOneOfPropertyElements(element)) return undefined;

  const propChange = tryRegularPropertyChange(element, change);
  if (propChange) return propChange;

  if (!projectGuards.is.element(element)) return undefined;
  return tryQuantityPropertyChange(element, change);
};

/** Apply resolved value on a regular element property (element.properties[]) */
const tryRegularPropertyChange = (
  element: OneOfPropertyElements,
  change: PropertyResolvedChange,
): Partial<OneOfElements> | undefined => {
  const props = getElementProperties(element);
  const idx = props.findIndex(
    (p) => isElementExpressionProperty(p) && p.name === change.propertyName,
  );
  if (idx === -1) return undefined;

  const prop = props[idx] as IElementExpressionProperty | undefined;
  if (!prop) return undefined;

  // Determine which field to write: count if it exists, fallbackCount otherwise
  const countField = prop.count
    ? 'count'
    : prop.fallbackCount
      ? 'fallbackCount'
      : undefined;
  if (!countField) return undefined;

  const base = prop[countField] as ExpressionValue;
  const formatted = getDisplayNumber(change.value);
  if (base.resolved === change.value && base.formatted === formatted) {
    return undefined;
  }

  const newProps = [...props];
  newProps[idx] = {
    ...prop,
    [countField]: { ...base, resolved: change.value, formatted },
  };

  return { properties: newProps };
};

/** Apply resolved value on a quantity property (element.quantity[name].count or .fallbackCount) */
const tryQuantityPropertyChange = (
  element: IElement,
  change: PropertyResolvedChange,
): Partial<OneOfElements> | undefined => {
  if (!element.quantity) return undefined;

  const quantityName = change.propertyName as ElementQuantityName;
  const quantity = element.quantity[quantityName];
  if (!quantity) return undefined;

  const countField =
    'count' in quantity && quantity.count
      ? 'count'
      : 'fallbackCount' in quantity && quantity.fallbackCount
        ? 'fallbackCount'
        : undefined;
  if (!countField) return undefined;

  const existing = quantity[countField as keyof typeof quantity];
  if (!guards.is.object(existing)) return undefined;

  const formatted = getDisplayNumber(change.value);
  if ('resolved' in existing && existing.resolved === change.value) {
    const prevFormatted =
      'formatted' in existing ? existing.formatted : undefined;
    if (prevFormatted === formatted) return undefined;
  }

  return {
    quantity: {
      ...element.quantity,
      [quantityName]: {
        ...quantity,
        [countField]: { ...existing, resolved: change.value, formatted },
      },
    },
  };
};
